import React, {useContext, useRef, useEffect, useState} from "react";
import {motion} from "framer-motion";
import "./WorkExperience.scss";
import {workExperiences} from "../../portfolio";
import StyleContext from "../../contexts/StyleContext";
import {fadeUp, vp} from "../../utils/animations";

// Company logo with initial-letter fallback if image fails to load
function CompanyLogo({src, alt, company}) {
  const [err, setErr] = useState(false);
  if (err)
    return <span className="exp__logo-initial">{company.charAt(0)}</span>;
  return (
    <img
      src={src}
      alt={alt}
      className="exp__logo"
      onError={() => setErr(true)}
    />
  );
}

export default function WorkExperience() {
  const {isDark} = useContext(StyleContext);
  const timelineRef = useRef(null);
  const [visible, setVisible] = useState(false);

  // Trigger timeline animation when section enters viewport
  useEffect(() => {
    const el = timelineRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      {threshold: 0.05}
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  if (!workExperiences.display) return null;

  return (
    <motion.section
      className={`exp ${isDark ? "dark" : ""}`}
      id="experience"
      initial="hidden"
      whileInView="show"
      viewport={vp}
      variants={fadeUp}
    >
      <div className="exp__inner">
        <motion.div className="exp__header" variants={fadeUp}>
          <span className="section-label">Experience</span>
          <h2 className="exp__title">Work Experience</h2>
        </motion.div>

        {/* ── Timeline ── */}
        <div
          ref={timelineRef}
          className={`exp__timeline${visible ? " exp__timeline--visible" : ""}`}
        >
          {workExperiences.experience.map((job, i) => {
            const baseDelay = i * 0.12;
            return (
              <div key={i} className="exp__item">
                {/* Timeline spine */}
                <div className="exp__spine">
                  <div
                    className="exp__dot"
                    style={{animationDelay: `${baseDelay}s`}}
                  />
                  {i < workExperiences.experience.length - 1 && (
                    <div
                      className="exp__line"
                      style={{animationDelay: `${baseDelay + 0.2}s`}}
                    />
                  )}
                </div>

                {/* Role content */}
                <div className="exp__content">
                  <div className="exp__meta">
                    <div className="exp__logo-wrap">
                      <CompanyLogo
                        src={job.companylogo}
                        alt={job.company}
                        company={job.company}
                      />
                    </div>
                    <div>
                      <h3 className="exp__role">{job.role}</h3>
                      <p className="exp__company">
                        {job.company}
                        <span className="exp__sep">·</span>
                        <span className="exp__date">{job.date}</span>
                        {job.location && (
                          <>
                            <span className="exp__sep">·</span>
                            <span className="exp__location">
                              {job.location}
                            </span>
                          </>
                        )}
                      </p>
                    </div>
                  </div>

                  {job.metrics && job.metrics.length > 0 && (
                    <div className="exp__card-metrics">
                      {job.metrics.map((m, k) => (
                        <div key={k} className="exp__card-metric">
                          <span className="exp__card-metric-val">
                            {m.value}
                          </span>
                          <span className="exp__card-metric-lbl">
                            {m.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {job.pmValue && (
                    <div className="exp__pm">
                      <span className="exp__pm-label">For teams</span>
                      <p className="exp__pm-text">{job.pmValue}</p>
                    </div>
                  )}

                  <div className="exp__blocks">
                    {job.systemBuilt && job.systemBuilt.length > 0 && (
                      <div className="exp__block">
                        <span className="exp__block-label">Built</span>
                        <ul className="exp__block-list">
                          {job.systemBuilt.map((b, j) => (
                            <li key={j}>{b}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="exp__block">
                      <span className="exp__block-label">Stack</span>
                      <div className="exp__tech">
                        {job.tech.map((t, k) => (
                          <span key={k} className="tech-tag">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </motion.section>
  );
}
